import { useRef, useState, useCallback } from 'react';
import { toast } from 'react-toastify';

// Helper to safely log environment variables without sensitive data
const safeEnvVars = {
  REACT_APP_WORKER_URL: process.env.REACT_APP_WORKER_URL || 'not set',
  NODE_ENV: process.env.NODE_ENV || 'not set',
  // Add other non-sensitive env vars you want to monitor
};

// Determine WORKER_URL with logging
const WORKER_URL = (() => {
  const url = process.env.REACT_APP_WORKER_URL || 'http://127.0.0.1:4242';
  
  console.info('[Environment Config] Current environment:', process.env.NODE_ENV);
  console.info('[Environment Config] Using WORKER_URL:', url);
  console.info('[Environment Config] Available env vars:', safeEnvVars);

  if (!url.startsWith('http')) {
    console.warn('[Environment Config] Warning: WORKER_URL does not start with http');
  }

  return url;
})();

interface WebRTCState {
  isInCall: boolean;
  chatText: string;
  isTalking: boolean;
}

interface ChatMessage {
  role: 'assistant' | 'user';
  text: string;
}

interface UseWebRTCOptions {
  onTextReceived?: (message: ChatMessage) => void;
  onTextDelta?: (delta: string) => void;
  onTextComplete?: () => void;
  onError?: (error: Error) => void;
  scenarioId?: number;
  voice?: string;
  modelVersion?: string;
}

export const useWebRTC = (options: UseWebRTCOptions = {}) => {
  const [state, setState] = useState<WebRTCState>({
    isInCall: false,
    chatText: '',
    isTalking: false
  });

  const peerConnection = useRef<RTCPeerConnection | null>(null);
  const dataChannel = useRef<RTCDataChannel | null>(null);
  const audioElement = useRef<HTMLAudioElement | null>(null);
  const sessionId = useRef<string | null>(null);
  const currentResponseId = useRef<string | null>(null);
  const talkingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const ephemeralToken = useRef<string | null>(null);
  const tokenExpiryTimeout = useRef<NodeJS.Timeout | null>(null);

  // Function to get a new ephemeral token
  const getEphemeralToken = useCallback(async () => {
    const userId = localStorage.getItem('userId');
    if (!userId || !options.scenarioId) {
      throw new Error('Missing userId or scenarioId');
    }

    const tokenResponse = await fetch(`${WORKER_URL}/api/ephemeral-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify({
        userId,
        scenarioId: options.scenarioId,
        voice: options.voice || 'alloy',
        modelVersion: options.modelVersion || 'gpt-4o-realtime-preview-2024-12-17'
      })
    });
    
    if (!tokenResponse.ok) {
      const errorText = await tokenResponse.text();
      console.error('Token error:', errorText);
      throw new Error('Failed to get ephemeral token');
    }

    const data = await tokenResponse.json();
    if (!data.ephemeralToken || !data.sessionId || !data.expiresAt) {
      throw new Error('Invalid ephemeral token response');
    }

    return {
      token: data.ephemeralToken,
      sessionId: data.sessionId,
      expiresAt: data.expiresAt
    };
  }, [options.scenarioId, options.voice, options.modelVersion]);

  // Function to schedule token refresh
  const scheduleTokenRefresh = useCallback((expiresAt: number) => {
    // Clear any existing timeout
    if (tokenExpiryTimeout.current) {
      clearTimeout(tokenExpiryTimeout.current);
    }

    // Calculate when to refresh (15 seconds before expiry)
    const refreshTime = expiresAt - Date.now() - 15000;
    
    if (refreshTime <= 0) {
      // Token is already expired or about to expire
      console.warn('[WebRTC] Token refresh scheduled immediately');
      tokenExpiryTimeout.current = setTimeout(async () => {
        try {
          const { token, expiresAt } = await getEphemeralToken();
          ephemeralToken.current = token;
          scheduleTokenRefresh(expiresAt);
        } catch (error) {
          console.error('[WebRTC] Failed to refresh token:', error);
          // If we can't refresh the token, end the call
          handleEndCall();
        }
      }, 0);
    } else {
      console.log(`[WebRTC] Scheduling token refresh in ${refreshTime}ms`);
      tokenExpiryTimeout.current = setTimeout(async () => {
        try {
          const { token, expiresAt } = await getEphemeralToken();
          ephemeralToken.current = token;
          scheduleTokenRefresh(expiresAt);
        } catch (error) {
          console.error('[WebRTC] Failed to refresh token:', error);
          handleEndCall();
        }
      }, refreshTime);
    }
  }, [getEphemeralToken]);

  const clearTalkingTimeout = () => {
    if (talkingTimeoutRef.current) {
      clearTimeout(talkingTimeoutRef.current);
      talkingTimeoutRef.current = null;
    }
  };

  const setTalkingState = (talking: boolean) => {
    clearTalkingTimeout();
    if (!talking) {
      // Add a small delay before setting isTalking to false
      talkingTimeoutRef.current = setTimeout(() => {
        console.log('Talking timeout completed, setting isTalking to false');
        setState(prev => ({ ...prev, isTalking: false }));
      }, 200);
    } else {
      setState(prev => ({ ...prev, isTalking: true }));
    }
  };

  const handleStartCall = useCallback(async () => {
    console.log('[WebRTC] Starting call...');
    try {
      // 1. Get initial ephemeral token
      const { token, sessionId: newSessionId, expiresAt } = await getEphemeralToken();
      ephemeralToken.current = token;
      sessionId.current = newSessionId;

      // Schedule token refresh
      scheduleTokenRefresh(expiresAt);

      // 2. Create peer connection
      const pc = new RTCPeerConnection();
      peerConnection.current = pc;

      // 3. Set up audio playback with proper event handling
      audioElement.current = document.createElement('audio');
      audioElement.current.autoplay = true;
      
      pc.ontrack = e => {
        console.log('[WebRTC] Received track:', e.track.kind);
        if (audioElement.current) {
          audioElement.current.srcObject = e.streams[0];
          
          audioElement.current.onplaying = () => {
            console.log('[WebRTC] Audio started playing');
            setState(prev => ({ ...prev, isTalking: true }));
          };
          
          audioElement.current.onpause = () => {
            // Don't set isTalking to false on pause, only on ended
            console.log('[WebRTC] Audio paused');
          };
          
          audioElement.current.onended = () => {
            console.log('[WebRTC] Audio ended');
            setState(prev => ({ ...prev, isTalking: false }));
          };

          audioElement.current.onerror = (e) => {
            console.error('[WebRTC] Audio playback error:', e);
            setState(prev => ({ ...prev, isTalking: false }));
          };

          e.track.onended = () => {
            console.log('[WebRTC] Audio track ended');
            setState(prev => ({ ...prev, isTalking: false }));
          };
        }
      };

      // 4. Add local audio track with specific constraints
      const ms = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          channelCount: 1,
          sampleRate: 48000,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });
      pc.addTrack(ms.getTracks()[0], ms);

      // 5. Create data channel
      const dc = pc.createDataChannel('oai-events');
      dataChannel.current = dc;
      
      dc.onopen = () => {
        console.log('Data channel opened');
        // Send session configuration
        const sessionConfig = {
          type: 'session.update',
          session: {
            modalities: ['audio', 'text'],
            input_audio_transcription: {
              model: 'whisper-1'
            },
            input_audio_format: 'pcm16',
            output_audio_format: 'pcm16',
            voice: options.voice || 'alloy',
            turn_detection: {
              type: 'server_vad',
              threshold: 0.5,
              prefix_padding_ms: 300,
              silence_duration_ms: 500,
              create_response: true
            },
            temperature: 0.7,
            instructions: `You are a potential client in a sales conversation.
              Key behavioral instructions:
              1. You are the BUYER, not the seller
              2. Stay in character as a potential client
              3. Respond naturally to questions and pitches
              4. Show appropriate interest or skepticism
              5. Use natural speech patterns
              6. Maintain consistent personality traits
              7. If the salesperson is too pushy, show resistance
              8. Ask relevant questions about the product/service`
          }
        };
        dc.send(JSON.stringify(sessionConfig));
      };
      
      dc.onmessage = (e) => {
        try {
          const data = JSON.parse(e.data);
          
          // Log all OpenAI events with a clear separator
          console.log('\n=== OPENAI EVENT ===');
          console.log('Type:', data.type);
          console.log('Data:', data);
          console.log('==================\n');

          // Handle specific events
          switch (data.type) {
            case 'conversation.item.input_audio_transcription.completed':
              if (data.transcript) {
                console.log('\n🎤 USER SAID:');
                console.log(data.transcript);
                console.log('============\n');
                options.onTextReceived?.({ role: 'user' as const, text: data.transcript });
              }
              break;

            case 'response.created':
              console.log('\n🤖 AI STARTING NEW RESPONSE');
              currentResponseId.current = data.response.id;
              break;

            case 'response.audio_transcript.delta':
              if (data.response_id === currentResponseId.current) {
                options.onTextDelta?.(data.delta);
              }
              break;

            case 'response.audio_transcript.done':
              console.log('\n✅ AI RESPONSE COMPLETE');
              options.onTextComplete?.();
              break;

            case 'input_audio_buffer.speech_started':
              console.log('\n🎤 USER STARTED SPEAKING');
              break;

            case 'input_audio_buffer.speech_stopped':
              console.log('🎤 USER STOPPED SPEAKING\n');
              break;

            case 'session.created':
              console.log('\n🔵 SESSION CREATED:', data.session);
              break;

            case 'session.updated':
              console.log('\n🔄 SESSION UPDATED:', data.session);
              break;

            case 'conversation.item.created':
              console.log('\n📝 NEW CONVERSATION ITEM:', data.item);
              break;

            case 'rate_limits.updated':
              console.log('\n⚡ RATE LIMITS UPDATED:', data.rate_limits);
              break;
          }
        } catch (error) {
          console.error('❌ [WebRTC] Error handling message:', error);
        }
      };

      // 6. Create and send offer
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      // 7. Send offer to server with session ID
      if (!sessionId.current || !ephemeralToken.current) {
        throw new Error('Session ID or ephemeral token not available');
      }

      const sdpResponse = await fetch(`${WORKER_URL}/api/webrtc-signaling`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/sdp',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'X-Session-ID': sessionId.current,
          'X-Ephemeral-Token': ephemeralToken.current
        },
        body: offer.sdp
      });

      if (!sdpResponse.ok) {
        const errorText = await sdpResponse.text();
        console.error('SDP error:', errorText);
        throw new Error('Failed to connect to server');
      }

      // 8. Set remote description
      const answer = {
        type: 'answer' as RTCSdpType,
        sdp: await sdpResponse.text()
      };
      await pc.setRemoteDescription(answer);

      setState(prev => ({ ...prev, isInCall: true }));
    } catch (error) {
      console.error('[WebRTC] Error in startCall:', error);
      if (error instanceof Error) {
        options.onError?.(error);
      }
      toast.error('Failed to start call');
      handleEndCall();
    }
  }, [options, getEphemeralToken, scheduleTokenRefresh]);

  const handleEndCall = useCallback(async () => {
    console.log('[WebRTC] Ending call...');
    // Clear token refresh timeout
    if (tokenExpiryTimeout.current) {
      clearTimeout(tokenExpiryTimeout.current);
      tokenExpiryTimeout.current = null;
    }
    ephemeralToken.current = null;
    clearTalkingTimeout();
    try {
      if (sessionId.current) {
        // Send end call request to server
        const response = await fetch(`${WORKER_URL}/api/end-call`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            sessionId: sessionId.current
          })
        });

        if (!response.ok) {
          console.error('Failed to end call properly:', await response.text());
        }
      }
    } catch (error) {
      console.error('[WebRTC] Error in endCall:', error);
    } finally {
      if (peerConnection.current) {
        peerConnection.current.close();
        peerConnection.current = null;
      }
      if (dataChannel.current) {
        dataChannel.current.close();
        dataChannel.current = null;
      }
      if (audioElement.current) {
        audioElement.current.srcObject = null;
        audioElement.current = null;
      }
      sessionId.current = null;
      currentResponseId.current = null;
      setState({ isInCall: false, chatText: '', isTalking: false });
    }
  }, []);

  return {
    ...state,
    startCall: handleStartCall,
    endCall: handleEndCall
  };
}; 